import React from 'react';
import { Warning, CheckCircle, TrendDown } from '@phosphor-icons/react';

interface AlertItem {
    alert_message?: string;
    alertMessage?: string;
    alert_type?: string;
    alertType?: string;
    session_name?: string;
    sessionName?: string;
    severity?: number;
    threshold?: number;
    value?: number;
    [key: string]: unknown;
}

interface AlertCardsProps {
    data: AlertItem[] | { data: AlertItem[]; metadata?: unknown; meta?: unknown; schema?: unknown };
    options?: {
        maxCards?: number;
    };
}

const getAlertTypeLabel = (type: string): string => {
    const t = (type || '').toLowerCase();
    if (t.includes('attendance')) return 'Low Attendance';
    if (t.includes('noshow') || t.includes('no_show') || t.includes('no-show')) return 'High No-Show';
    if (t.includes('declining') || t.includes('trend')) return 'Declining Trend';
    return type || 'Alert';
};

const getSeverityConfig = (value: number) => {
    if (value < 25) return { border: 'border-l-red-500', badge: 'bg-red-100 text-red-700 border border-red-200', label: 'Critical', barColor: 'bg-red-500', textColor: 'text-red-600' };
    if (value < 35) return { border: 'border-l-orange-400', badge: 'bg-orange-100 text-orange-700 border border-orange-200', label: 'High', barColor: 'bg-orange-500', textColor: 'text-orange-600' };
    return { border: 'border-l-amber-400', badge: 'bg-amber-100 text-amber-700 border border-amber-200', label: 'Medium', barColor: 'bg-amber-500', textColor: 'text-amber-600' };
};

export const AlertCards: React.FC<AlertCardsProps> = ({ data, options = {} }) => {
    const { maxCards = 5 } = options;

    let items: AlertItem[] = [];
    if (Array.isArray(data)) {
        items = data;
    } else if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        items = data.data;
    }

    const displayItems = items.slice(0, maxCards);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-3">
                    <CheckCircle size={28} weight="fill" className="text-green-400" />
                </div>
                <span className="text-sm font-medium text-gray-600">No issues detected</span>
                <span className="text-xs text-gray-400 mt-1">All sessions are on track</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2.5 p-3 h-full overflow-auto">
            {displayItems.map((item, index) => {
                const sessionName = item.sessionName || item.session_name || 'Unknown Session';
                const alertType = item.alertType || item.alert_type || 'alert';
                const value = Number(item.value ?? 0);
                const threshold = Number(item.threshold ?? 50);
                const gap = Math.round((threshold - value) * 10) / 10;
                const progressPct = Math.min((value / threshold) * 100, 100);

                const config = getSeverityConfig(value);
                const typeLabel = getAlertTypeLabel(alertType);

                return (
                    <div
                        key={`alert-${index}`}
                        className={`group bg-white rounded-lg border border-gray-200 border-l-4 ${config.border} p-3.5 hover:shadow-sm transition-all`}
                    >
                        {/* Row 1: Type label + Severity badge */}
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1.5">
                                <Warning size={14} weight="bold" className={config.textColor} />
                                <span className={`text-[11px] font-semibold uppercase tracking-wide ${config.textColor}`}>
                                    {typeLabel}
                                </span>
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${config.badge}`}>
                                {config.label}
                            </span>
                        </div>

                        {/* Row 2: Session name */}
                        <h3 className="text-sm font-semibold text-gray-900 mb-2.5 truncate" title={sessionName}>
                            {sessionName}
                        </h3>

                        {/* Row 3: Progress bar */}
                        <div className="flex items-center gap-3 mb-2">
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ${config.barColor}`}
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                            <span className="text-xs font-bold text-gray-700 min-w-[36px] text-right">
                                {value}%
                            </span>
                        </div>

                        {/* Row 4: Gap text */}
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <TrendDown size={12} className={config.textColor} />
                            <span>
                                Below threshold by <span className={`font-semibold ${config.textColor}`}>{gap}%</span>
                            </span>
                            <span className="text-gray-300 mx-1">·</span>
                            <span className="text-gray-400">Target: {threshold}%</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
